<script lang="ts">
  import cn from 'classnames'
  import { onDestroy, onMount } from 'svelte'

  import { loadStylesheet } from '#js/common/helpers'
  import { RatingVote, type RatingVoteEventDetails } from '#js/common/apis'

  import SVG from './SVG_svelte.svelte'

  import styles from '#css/v9/components/rating-spinner.pcss.json'

  export let targetType: string
  export let targetId: number
  export let value: number = 0
  export let isGuest: boolean
  export let canVote: boolean
  export let vote: '+' | '-' | null = null
  export let link: string = ''
  export let disableTabIndex: boolean = false
  export let showDisabledButtons: boolean = false
  export let disableMinusButton: boolean = false
  export let outline: boolean = false
  export let updateRating: (value: number, voteDirection: '+' | '-' | null) => void

  const rateSuccess = (event: CustomEvent<RatingVoteEventDetails>) => {
    const {targetType: eventTargetType, targetId: eventTargetId, result} = event.detail

    if (eventTargetType === targetType && eventTargetId === targetId) {
      if (updateRating) {
        updateRating(result.value, result.voteDirection)
        return
      }
      value = result.value
      vote = result.voteDirection
    }
  }

  onMount(() => {
    loadStylesheet('v9/components/rating-spinner')
    window.addEventListener('sg-rating-vote', rateSuccess)
  })

  onDestroy(() => {
    window.removeEventListener('sg-rating-vote', rateSuccess)
  })

  const doVote = (value: '-' | '+') => {
    if (link && link !== '') {
      window.location.href = link
      return
    }

    RatingVote(targetType, targetId, value)
  }

  const voteMinus = (ev: MouseEvent) => {
    ev.stopImmediatePropagation()
    ev.preventDefault()
    doVote('-')
  }

  const votePlus = (ev: MouseEvent) => {
    ev.stopImmediatePropagation()
    ev.preventDefault()
    doVote('+')
  }

  let ratingValue: string;
  let tabindex = 0

  if (disableTabIndex) {
    tabindex = -1
  }

  let minusClasses: string;
  let plusClasses: string;
  let ratingClasses: string;

  $: {
    ratingValue = `${value}`
    if (value > 0) {
      ratingValue = `+${value}`
    }

    minusClasses = cn({
      [styles['rating-spinner__button']]: true,
      [styles['rating-spinner__minus']]: true,
      [styles['active']]: vote === '-'
    })
    plusClasses = cn({
      [styles['rating-spinner__button']]: true,
      [styles['rating-spinner__plus']]: true,
      [styles['active']]: vote === '+'
    })
    ratingClasses = cn({
      [styles['rating-spinner__rating']]: true,
      [styles['rating-spinner__rating--positive']]: value > 0,
      [styles['rating-spinner__rating--negative']]: value < 0
    })
  }
</script>

<div class={cn({
    [styles['rating-spinner']]: true,
    [styles['rating-spinner--outline']]: outline
})}>
    {#if !disableMinusButton && (canVote || showDisabledButtons)}
        {#if isGuest}
            <button type="button" class={minusClasses} data-plugins="auth" tabindex={tabindex}>
                <SVG i="sg-new/minus" adaptive/>
            </button>
        {:else}
            <button
                type="button"
                class={minusClasses}
                on:click={voteMinus}
                disabled={!canVote || vote === '-'}
                tabindex={tabindex}
            >
                <SVG i="sg-new/minus" adaptive/>
            </button>
        {/if}
    {/if}
    <span class={ratingClasses}>{ratingValue}</span>
    {#if (canVote || showDisabledButtons)}
        {#if isGuest}
            <button type="button" class={plusClasses} data-plugins="auth" tabindex={tabindex}>
                <SVG i="sg-new/plus" adaptive/>
            </button>
        {:else}
            <button
                type="button"
                class={plusClasses}
                on:click={votePlus}
                disabled={!canVote || (!disableMinusButton && vote === '+')}
                tabindex={tabindex}
            >
                <SVG i="sg-new/plus" adaptive/>
            </button>
        {/if}
    {/if}
</div>
